/**
 * AI 物品提案类型定义
 * 目标：为“LLM 生成 -> 本地裁决 -> ItemBaseTemplate”提供中间契约
 */

import { z } from 'zod';
import { RankSchema } from '@/IsekaiRPG/shared/types';
import {
  EquipmentSlotSchema,
  ItemAffixCategorySchema,
  ItemCategorySchema,
  ItemRaritySchema,
  ItemRequirementSchema,
  ItemSkillBonusSchema,
} from './item';

// ============================================
// LLM 物品提案
// ============================================

/**
 * LLM 物品模板提案
 * 说明：本结构允许一定冗余字段，最终以本地裁决结果为准。
 */
export const ItemTemplateProposalSchema = z.object({
  id: z.string().min(1).optional(),
  name: z.string().min(1),
  category: ItemCategorySchema,
  rarity: ItemRaritySchema.default('common'),
  description: z.string().default(''),
  icon: z.string().default(''),
  tags: z.array(z.string()).default([]),
  unique: z.boolean().default(false),
  stackable: z.boolean().optional(),
  equipSlot: z.preprocess(
    value => (value === '' || value === null ? undefined : value),
    EquipmentSlotSchema.optional(),
  ),
  levelRequirement: z.number().int().min(0).default(0),
  price: z.number().int().min(0).default(0),
  baseStats: z.record(z.string(), z.number()).default({}),
  // 提案层允许 LLM 输出多种写法，这里统一过滤为系统可执行结构
  requirements: z
    .array(z.unknown())
    .default([])
    .transform((items): z.infer<typeof ItemRequirementSchema>[] =>
      items
        .map(item => ItemRequirementSchema.safeParse(item))
        .filter(result => result.success)
        .map(result => result.data),
    ),
  bonuses: z
    .array(z.unknown())
    .default([])
    .transform((items): z.infer<typeof ItemSkillBonusSchema>[] =>
      items
        .map(item => ItemSkillBonusSchema.safeParse(item))
        .filter(result => result.success)
        .map(result => result.data),
    ),
  affixBudget: z.number().min(0).optional(),
  allowedAffixCategories: z.array(ItemAffixCategorySchema).optional(),
});
export type ItemTemplateProposal = z.infer<typeof ItemTemplateProposalSchema>;

// ============================================
// 生成上下文
// ============================================

export const ItemGenerationContextSchema = z.object({
  world: z
    .object({
      country: z.string().optional(),
      zoneName: z.string().optional(),
      zoneType: z.string().optional(),
    })
    .optional(),
  party: z
    .object({
      size: z.number().int().min(0).optional(),
      recommendedLevel: z.number().int().min(1).optional(),
    })
    .optional(),
  source: z
    .object({
      sourceType: z.enum(['quest_reward', 'enemy_drop', 'shop', 'craft', 'event']).optional(),
      sourceId: z.string().optional(),
      difficultyRank: RankSchema.optional(),
    })
    .optional(),
  hint: z.string().optional(),
});
export type ItemGenerationContext = z.infer<typeof ItemGenerationContextSchema>;
